import { apiClient } from '@/api/client'
import type {
  CreateReadHistoryRequest,
  EpisodeProgress,
  NextTiming,
  ProductProgress,
  ProductProgressListResponse,
  ReadHistory,
  ReadHistoryListResponse,
  UpdateEpisodeProgressRequest,
  UpdateReadHistoryRequest,
} from '@/api/type'
import { parseUtcDate } from '@/lib/date-utils'
import {
  cacheSyncedProgress,
  getOfflineProgress,
  markProgressSynced,
  type OfflineProgress,
  saveOfflineProgress,
} from '@/lib/db'

// ============================================================
// ローカル進捗（IndexedDB）
// 機内モードでもダウンロード済みの作品を続きから開けるよう、進捗は端末にも持つ。
// オフライン中の更新は useSyncEngine がオンライン復帰時にサーバへ送る。
// ============================================================

const toEpisodeProgress = (p: OfflineProgress, playCount = 0): EpisodeProgress => ({
  episode_id: p.episodeId,
  progress: p.progress,
  cfi: p.cfi,
  locator: p.locator ?? null,
  is_completed: p.isCompleted,
  play_count: playCount,
  updated_at: new Date(p.updatedAt).toISOString(),
})

const readLocalProgress = async (episodeId: number): Promise<OfflineProgress | undefined> => {
  try {
    return await getOfflineProgress(episodeId)
  } catch (error) {
    console.warn('[progress] ローカル進捗の読み込みに失敗:', error)
    return undefined
  }
}

/** サーバから取得した進捗を端末に保存する（未同期で新しいローカルの記録は残す） */
export const cacheEpisodeProgress = async (server: EpisodeProgress): Promise<void> => {
  try {
    await cacheSyncedProgress({
      episodeId: server.episode_id,
      progress: server.progress,
      cfi: server.cfi,
      locator: server.locator,
      isCompleted: server.is_completed,
      updatedAt: parseUtcDate(server.updated_at).getTime(),
      synced: 1,
    })
  } catch (error) {
    console.warn('[progress] ローカル進捗の保存に失敗:', error)
  }
}

/** 更新内容を未同期として端末に保存する（省略された項目は前回の値を引き継ぐ） */
const saveLocalProgress = async (
  episodeId: number,
  request: UpdateEpisodeProgressRequest,
): Promise<OfflineProgress | null> => {
  const prev = await readLocalProgress(episodeId)
  const record: OfflineProgress = {
    episodeId,
    progress: request.progress,
    cfi: request.cfi !== undefined ? request.cfi : (prev?.cfi ?? null),
    locator: request.locator !== undefined ? request.locator : (prev?.locator ?? null),
    isCompleted: request.is_completed ?? prev?.isCompleted ?? false,
    updatedAt: Date.now(),
    synced: 0,
  }
  try {
    await saveOfflineProgress(record)
    return record
  } catch (error) {
    console.warn('[progress] ローカル進捗の保存に失敗:', error)
    return null
  }
}

// ============================================================
// 進捗API
// ============================================================

/**
 * 最近読んだプロダクト一覧を取得
 *
 * excludeSafeHidden を立てると、Safe モードで隠すドメインをユーザーの設定に関係なく除く
 */
export const getRecentProducts = async (
  limit: number = 20,
  excludeSafeHidden: boolean = false,
): Promise<ProductProgressListResponse> => {
  const res = await apiClient.get('/users/progress/products', {
    params: { limit, exclude_safe_hidden: excludeSafeHidden },
  })
  return res.data
}

/**
 * 単一プロダクトの進捗を取得
 */
export const getProductProgress = async (productId: number): Promise<ProductProgress | null> => {
  const res = await apiClient.get(`/users/progress/products/${productId}`)
  return res.data
}

/**
 * エピソード進捗を取得
 *
 * オフライン時や通信に失敗したときは端末に保存した進捗を返す。
 * オンラインでも、未同期のローカルの進捗の方が新しければそちらを返す。
 */
export const getEpisodeProgress = async (episodeId: number): Promise<EpisodeProgress | null> => {
  const local = await readLocalProgress(episodeId)
  if (!navigator.onLine) return local ? toEpisodeProgress(local) : null

  let server: EpisodeProgress | null
  try {
    const res = await apiClient.get<EpisodeProgress | null>(`/users/progress/episodes/${episodeId}`)
    server = res.data
  } catch (error) {
    if (local) return toEpisodeProgress(local)
    throw error
  }

  if (!server) return local && !local.synced ? toEpisodeProgress(local) : null
  if (local && !local.synced && local.updatedAt > parseUtcDate(server.updated_at).getTime()) {
    return toEpisodeProgress(local, server.play_count)
  }
  await cacheEpisodeProgress(server)
  return server
}

/**
 * プロダクト内の全エピソード進捗を一括取得
 */
export const getProductEpisodesProgress = async (productId: number): Promise<EpisodeProgress[]> => {
  const res = await apiClient.get(`/users/progress/products/${productId}/episodes`)
  return res.data
}

/**
 * エピソード進捗を更新
 *
 * 先に端末へ保存し、オフライン時はその内容を返す（サーバへは useSyncEngine が後で送る）。
 * increment_play_count はオフライン中は反映されない。
 */
export const updateEpisodeProgress = async (
  episodeId: number,
  request: UpdateEpisodeProgressRequest,
): Promise<EpisodeProgress> => {
  const local = await saveLocalProgress(episodeId, request)
  if (local && !navigator.onLine) return toEpisodeProgress(local)

  const res = await apiClient.put<EpisodeProgress>(`/users/progress/episodes/${episodeId}`, request)
  if (local) {
    await markProgressSynced(episodeId, local.updatedAt).catch((error: unknown) => {
      console.warn('[progress] 同期済みフラグの更新に失敗:', error)
    })
  }
  return res.data
}

/**
 * 作品ごとに学習した、次の動画プレビューの基準となる残り秒数を取得
 */
export const getNextTiming = async (productId: number): Promise<NextTiming> => {
  const res = await apiClient.get<NextTiming>(`/users/progress/products/${productId}/next-timing`)
  return res.data
}

/**
 * 「次へ」を押した（またはプレビューを閉じた）残り秒数を学習データに追加
 */
export const addNextTimingSample = async (productId: number, remainingSeconds: number): Promise<NextTiming> => {
  const res = await apiClient.post<NextTiming>(`/users/progress/products/${productId}/next-timing`, {
    remaining_seconds: remainingSeconds,
  })
  return res.data
}

// ============================================================
// 履歴API
// ============================================================

/**
 * UTC時刻文字列を確実にUTCとしてパースできるよう正規化
 * バックエンドがナイーブなISO文字列（Zなし）を返すため、末尾にZを付加して
 * ブラウザのDateがUTCとして正しく解釈できるようにする
 */
const ensureUTCSuffix = (utcString: string): string => {
  if (utcString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(utcString)) {
    return utcString
  }
  return `${utcString}Z`
}

/**
 * 読書セッションを開始
 */
export const createReadHistory = async (request: CreateReadHistoryRequest): Promise<ReadHistory> => {
  const res = await apiClient.post('/users/history', request)
  return res.data
}

/**
 * 読書セッションを更新
 */
export const updateReadHistory = async (historyId: number, request: UpdateReadHistoryRequest): Promise<ReadHistory> => {
  const res = await apiClient.put(`/users/history/${historyId}`, request)
  return res.data
}

/**
 * 読書履歴一覧を取得
 * バックエンドがナイーブUTCで返すため、Zサフィックスを付加して返す
 */
export const getReadHistoryList = async (
  limit: number = 100,
  date?: string,
  episodeId?: number,
): Promise<ReadHistoryListResponse> => {
  const res = await apiClient.get<ReadHistoryListResponse>('/users/history', {
    params: { limit, date, episode_id: episodeId },
  })

  // ナイーブUTC → UTC(Z付き)に正規化
  const items = res.data.items.map((item) => ({
    ...item,
    started_at: ensureUTCSuffix(item.started_at),
    ended_at: ensureUTCSuffix(item.ended_at),
  }))

  return { items }
}

/**
 * 履歴1件を削除
 */
export const deleteReadHistory = async (historyId: number): Promise<void> => {
  await apiClient.delete(`/users/history/${historyId}`)
}

/**
 * エピソードの履歴を全削除
 */
export const deleteEpisodeHistory = async (episodeId: number): Promise<void> => {
  await apiClient.delete(`/users/history/episode/${episodeId}`)
}

/**
 * プロダクトの履歴を全削除
 */
export const deleteProductHistory = async (productId: number): Promise<void> => {
  await apiClient.delete(`/users/history/product/${productId}`)
}
